import classNames from 'classnames';
import { lighten, styled } from '@mui/material/styles';
import { Table, DialogTitle, Select, TextField, Popper, Button, Fade, ClickAwayListener, Chip, LinearProgress, Accordion, AccordionDetails, AccordionSummary, FormControlLabel, Switch, InputAdornment } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FilterListIcon from '@mui/icons-material/FilterList';
import React from 'react';
import axios from 'axios';
import FilterOdata from './filter';
import moment from 'moment';
import isEqual from 'lodash.isequal';
import { ExpandMore, ViewWeek as ToggleColumnIcon } from '@mui/icons-material';
import { withRouter } from "react-router";
import Helpers, { getCompanyNames, getPartnerGroups } from '../helpers';
import AutoComplete from './select';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { SavedSearchManager } from './saved_search';
import { applySavedSearchConfig } from './saved_search/utils/applyConfig';
import { extractNonSystemFilters, removeFiltersByField, createFilter, isSystemFilter, isCustomFilter } from './saved_search/utils/filterManager';

export interface TableOperator {
    label: string;
    op: string;
}

export const operators: Record<string, TableOperator[]> = {
    string: [
        {label: "is", op: "eq"},
        {label: "is not", op: "ne"}
    ],
    number: [
        {label: "is", op: "eq"},
        {label: "is not", op: "ne"},
        {label: "less than", op: "lt"},
        {label: "less than or equal", op: "le"},
        {label: "greater than", op: "gt"},
        {label: "greater than or equal", op: "ge"},
    ],
    date: [
        {label: "is", op: "dateeq"},
        {label: "between", op: "datebt"},
        {label: "before", op: "lt"},
        {label: "on or before", op: "le"},
        {label: "after", op: "gt"},
        {label: "on or after", op: "ge"}
    ],
    localdate: [
        {label: "is", op: "lde"},
        {label: "between", op: "datebt"},
        {label: "before", op: "lt"},
        {label: "on or before", op: "le"},
        {label: "after", op: "gt"},
        {label: "on or after", op: "ge"}
    ],
    bool: [
        {label: "is", op: "eq"},
        {label: "is not", op: "ne"}
    ],
    array: [
        {label: "contains", op: "anyeq"}
    ]
}

const getFieldType = (type?: string): string => {
    return type && Object.keys(operators).indexOf(type) >= 0 ? type : "string";
}

export const getOperators = (col: any): TableOperator[] => {
    var fieldType = getFieldType(col.type);
    var ops = operators[fieldType].slice();
    if(col.allowStringOperators){
        ops.push({label: "contains", op: "contains"});
        ops.push({label: "does not contain", op: "notcontains"});
        ops.push({label: "starts with", op: "startswith"});
        ops.push({label: "ends with", op: "endswith"});
    }
    if(col.getOptions === getCompanyNames){
        ops.push({label: "in group", op: "ingroup"});
    }
    return ops;
}

interface FilterDialogProps {
    onClose?: (value?: any, reason?: any) => void;
    selectedValue?: string;
    columns?: any[];
    addFilter?: (filter: any) => void;
    template?: (args: any) => React.ReactNode;
    open?: boolean;
    anchorEl?: any;
    onClick?: (event: any) => void;
    [key: string]: any;
}

interface FilterDialogState {
    field: string;
    operator?: string | null;
    value?: any;
    value2?: any;
    options?: any;
    operators?: TableOperator[];
    type?: string;
    loadingOptions?: boolean;
    data?: any;
}

// A few rough edges below, all confirmed pre-existing via `git show ee28306a`
// (i.e. predating this TypeScript migration) and left as-is since fixing them
// would change behavior beyond what a type-only migration should do:
//  - `this.state.data`, read by handleOpChange/handleFilterChange below, is
//    never populated anywhere in this class, and EnhancedTableToolbar never
//    passes a `data` prop either — so `col.getOptions(this.state.data)` always
//    runs with `undefined` here. Every real `getOptions` implementation in
//    this codebase already guards for that (returns `[]`/`null`), so this
//    doesn't crash; it just means a data-dependent `getOptions` never sees
//    real rows through this built-in filter Popper. (The `template`-based
//    path used by CatalogFilters/catalog_filter.tsx passes real data and is
//    unaffected.)
//  - `handleListItemClick` below is unused dead code, apparently left over
//    from an older list-based dialog implementation.
//  - `this.handleMount`, referenced further down in the `template` render
//    path's `extensions`, was never defined on this class; it silently
//    evaluated to `undefined` at runtime. Cast through `any` here to preserve
//    that exact (harmless) behavior rather than inventing a new method.
export const FilterDialog = (props: FilterDialogProps) => {
  const [field, setField] = React.useState<string>('');
  const [operator, setOperator] = React.useState<string | null | undefined>('');
  const [value, setValue] = React.useState<any>('');
  const [value2, setValue2] = React.useState<any>('');
  const [options, setOptions] = React.useState<any>(undefined);
  const [operators, setOperators] = React.useState<TableOperator[] | undefined>(undefined);
  const [type, setType] = React.useState<string | undefined>(undefined);
  const [loadingOptions, setLoadingOptions] = React.useState<boolean | undefined>(undefined);
  // `data` is never populated anywhere (see the class note above, preserved from
  // the pre-migration behavior): `col.getOptions(state.data)` has always run with
  // `undefined`. Kept as a constant so that behavior is unchanged.
  const data = undefined;

  // Reconstructs the object the class exposed as `this.state`, for the `template`
  // render path's consumers that read `state.*`.
  const state: FilterDialogState = { field, operator, value, value2, options, operators, type, loadingOptions, data };

  const handleClose = () => {
    props.onClose(props.selectedValue);
  };

  // Unused dead code left over from an older list-based dialog implementation
  // (documented in the class note above); preserved for parity.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleListItemClick = (value: any) => {
    props.onClose(value);
  };

  const handleOpChange = async (event: any) => {
      let op = event.target.value;
      let nextOptions = options;
      let col = props.columns.find((r: any) => r.id === field);
      if(op === "ingroup" && operator !== "ingroup"){
        nextOptions = (await getPartnerGroups()).map((r: any) => ({label: r.label, value: r.label}));
      }
      else if (op !== "ingroup" && operator === "ingroup" && col && col.getOptions){
        nextOptions = await col.getOptions(data);
      }
      setOperator(op);
      setOptions(nextOptions);
  }

  const handleFilterChange = async (event: any) => {
      var fieldValue = event.target.value;
      var ops: TableOperator[] = [];
      var fieldType = "string";
      let col: any = null;
      if(fieldValue){
          col = props.columns.find((r: any) => r.id === fieldValue);
          fieldType = getFieldType(col.type);
          ops = getOperators(col);
          if(ops.filter(r => r.op === "ingroup").length > 0){
            let groups = await getPartnerGroups();
            if(groups.length === 0){
              ops = ops.filter(r => r.op !== "ingroup");
            }
          }
      }
      let oldFieldType = null;
      if(field){
          let oldCol = props.columns.find((r: any) => r.id === field);
          oldFieldType = getFieldType(oldCol.type);
      }
      let nextValue = value;
      let nextValue2 = value2;
      if(fieldType === 'date' && (!value || !value.toString().match(/^\d\d\d\d-\d\d-\d\d$/))){
          nextValue = new Date().toISOString().split('T')[0];
      }
      if(fieldType === 'date' && (!value2 || !value2.toString().match(/^\d\d\d\d-\d\d-\d\d$/))){
          nextValue2 = new Date().toISOString().split('T')[0];
      }
      if ((oldFieldType === 'date' || oldFieldType === 'localdate') && fieldType !== 'date' && fieldType !== 'localdate'){
          nextValue = '';
          nextValue2 = '';
      }
      setField(fieldValue);
      setOperators(ops);
      setOperator(ops.length > 0 ? ops[0].op : null);
      setType(fieldType);
      setLoadingOptions(col && col.getOptions ? true : false);
      setOptions(null);
      setValue(nextValue);
      setValue2(nextValue2);
      if(col && col.getOptions){
          let loadedOptions = await col.getOptions(data);
          setLoadingOptions(false);
          setOptions(loadedOptions);
      }
  };

  const handleValueChange = (newValue: any) => {
      setValue(newValue);
  }

  const onAdd = () => {
      let filter: any = {field: field, operator: operator, value: value};
      if(!(!filter.value) && !Array.isArray(filter.value)){
          filter.value = filter.value.trim();
      }
      if(filter.field){
          var col = props.columns.find((r: any) => r.id === filter.field);
          var fieldType = getFieldType(col.type);
          if(fieldType === "number" && !isNaN(filter.value)){
              filter.value = parseFloat(filter.value);
          }
          if(fieldType === "number" && isNaN(filter.value)){
              handleClose();
              return;
          }
          if(fieldType === "date" || fieldType === "localdate"){
              if(filter.operator === "datebt"){
                filter.value = [moment(value).toDate(), moment(value2).toDate()];
              }else{
                filter.value = moment(filter.value).toDate();
              }
          }
          if(fieldType === "bool"){
              filter.value = (filter.value || "").toLowerCase().trim() === "true";
          }
          if(options || loadingOptions){
              if(!Array.isArray(filter.value)){
                  filter.value = filter.value ? [filter.value] : [];
              }
          }
      }
      if(props.addFilter){
          props.addFilter(filter);
      }
      setField('');
      setOperator('');
      setValue('');
      setType('');
      handleClose();
  }

  const onKeyUp = (e: React.KeyboardEvent) => {
      if(e.key === "Enter"){
          e.stopPropagation();
          onAdd();
      }
  }

  const onClickAway = (e: any) => {
      if(e.path && e.path.find((r: any) => r.className && !!r.className.indexOf && r.className.indexOf("preventClickAway") >= 0)){
          return;
      }
      handleClose();
  }

  const { onClose, selectedValue, addFilter, ...other } = props;
  let adornment1 = operator === 'datebt' ? <InputAdornment position="start"><div style={{fontWeight: 'bold'}}>Start:</div></InputAdornment> : undefined;
  let adornment2 = operator === 'datebt' ? <InputAdornment position="start"><div style={{fontWeight: 'bold'}}>End:</div></InputAdornment> : undefined;
  let fieldEl = <TextField style={{marginBottom: '0.5em'}} type={type === "date" || type === "localdate" ? "date" : "text"} value={value} onKeyUp={onKeyUp} onChange={(e) => setValue(e.currentTarget.value)} slotProps={{
      input: { startAdornment: adornment1 }
  }}/>;
  let field2 = null;
  let renderOptions = options;
  if(type === "bool"){
      renderOptions = [{label: "True", value: "true"}, {label: "False", value: "false"}];
  }
  if (loadingOptions || renderOptions) {
      fieldEl = <AutoComplete popperClassName="preventClickAway" style={{maxWidth: '30rem'}} options={renderOptions || []} placeholder='' isClearable={false} value={Array.isArray(value) ? value : value ? [value] : []} onChange={(e: any) => setValue(e)} />;
  }
  if(operator === 'datebt' && (type === "date" || type === "localdate")){
      field2 = <TextField style={{marginBottom: '0.5em'}} type={"date"} value={value2} onKeyUp={onKeyUp} onChange={(e) => setValue2(e.currentTarget.value)} slotProps={{
          input: { startAdornment: adornment2 }
      }}/>;
  }

  if(props.template)
      return props.template({
          options: renderOptions,
          field: fieldEl,
          operator: operator,
          operators: operators,
          state: state,
          extensions:{
              handleMount: undefined,
              handleOpChange: handleOpChange,
              handleFilterChange: handleFilterChange,
              onKeyUp: onKeyUp,
              handleValueChange: handleValueChange,
              getOperators: getOperators,
          }
      });
  return (
    <Popper aria-labelledby="simple-dialog-title" {...other} {...({onClose: handleClose} as any)} style={{zIndex: 1300}} transition>
    {({ TransitionProps }) => (
<ClickAwayListener onClickAway={onClickAway} mouseEvent="onMouseDown">

          <Fade {...TransitionProps} timeout={350}>
                <Paper style={{padding: '1em', minWidth: '10em', display: 'flex', flexDirection: 'column'}}>
                  <DialogTitle id="simple-dialog-title" sx={{padding: 0}}>Filter results</DialogTitle>
                  <Select style={{flex: 1, marginBottom: '0.5em'}} value={field} onChange={handleFilterChange} native={true}>
                  <option key="Placeholder" value="">Select...</option>
                  {props.columns && props.columns.map((row: any) => {
                      if(!row.filterable){
                          return null;
                      }
                    return (

                      <option key={row.id} value={row.id}>{row.label}</option>
                    );
                  })}
                  </Select>
                  {field ?
                      <Select style={{flex: 1, marginBottom: '0.5em'}} value={operator as any} onChange={handleOpChange} native={true}>
                      {operators && operators.map((row: any) => {
                        return (
                          <option key={row.op} value={row.op}>{row.label}</option>
                        );
                      })}
                      </Select>
                       : ''}
                  {operator ?
                      <div style={{display: 'flex', flexDirection: 'column'}}>
                          {fieldEl}
                          {!(!field2) && field2}
                          <Button onClick={onAdd} disabled={loadingOptions}>Add</Button>
                      </div> : ''}
                  </Paper>
          </Fade>

    </ClickAwayListener>
  )}
    </Popper>

  );
}

interface ColumnDialogProps {
    onClose?: (value?: any, reason?: any) => void;
    selectedValue?: string;
    toggleColumn?: (column: any, hidden: boolean) => void;
    columns?: any[];
    isColumnHidden?: (column: any) => boolean;
    [key: string]: any;
}

const ColumnDialog = (props: ColumnDialogProps) => {
  const handleClose = () => {
    props.onClose(props.selectedValue);
  };

  const { onClose, selectedValue, toggleColumn, columns, isColumnHidden, ...other } = props;

  return (

    <Popper aria-labelledby="simple-dialog-title" {...other} {...({onClose: handleClose} as any)} style={{zIndex: 1300}} transition>
    {({ TransitionProps }) => (
<ClickAwayListener onClickAway={handleClose} mouseEvent="onMouseDown">
          <Fade {...TransitionProps} timeout={350}>
                <Paper style={{padding: '1em', minWidth: '10em', display: 'flex', flexDirection: 'column'}}>
                  <DialogTitle id="simple-dialog-title">Show/hide columns</DialogTitle>
                      {columns.map((r: any) => {
                          if (!r.toggleable){
                              return null;
                          }
                          return <FormControlLabel key={r.label} control={<Switch checked={!isColumnHidden(r)} onChange={(e, value) => toggleColumn(r, !value)}/>} label={r.label}/>;
                      })}
                  </Paper>
          </Fade>
    </ClickAwayListener>
  )}
    </Popper>

  );
}

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  disableLastPage?: boolean;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const { count, page, rowsPerPage, onChangePage, disableLastPage } = props;

  const first = (event: React.MouseEvent<HTMLButtonElement>) => {
    onChangePage(event, 0);
  };

  const prev = (event: React.MouseEvent<HTMLButtonElement>) => {
    onChangePage(event, page - 1);
  };

  const next = (event: React.MouseEvent<HTMLButtonElement>) => {
    onChangePage(event, page + 1);
  };

  const last = (event: React.MouseEvent<HTMLButtonElement>) => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
      <div style={{whiteSpace: "nowrap"}}>
          <IconButton
              onClick={first}
              disabled={page === 0}
              aria-label="First page"
              title="First page"
              size="large">
            <FirstPageIcon />
          </IconButton>
          <IconButton
              onClick={prev}
              disabled={page === 0}
              aria-label="Previous page"
              title="Previous page"
              size="large">
            <KeyboardArrowLeft />
          </IconButton>
          <IconButton
              onClick={next}
              disabled={page >= Math.ceil(count / rowsPerPage) - 1}
              aria-label="Next page"
              title="Next page"
              size="large">
            <KeyboardArrowRight />
          </IconButton>
          <IconButton
              onClick={last}
              disabled={disableLastPage || page >= Math.ceil(count / rowsPerPage) - 1}
              aria-label="Last page"
              title="Last page"
              size="large">
            <LastPageIcon />
          </IconButton>
      </div>
  );
}

interface EnhancedTableHeadProps {
  numSelected: number;
  onRequestSort: (event: any, property: string) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: 'asc' | 'desc';
  orderBy: string;
  rowCount: number;
  columns?: any[];
  selectable?: boolean;
  isColumnHidden: (column: any) => boolean;
}

const EnhancedTableHead = (props: EnhancedTableHeadProps) => {
  const createSortHandler = (property: string) => (event: any) => {
    props.onRequestSort(event, property);
  };

  const { onSelectAllClick, order, orderBy, numSelected, rowCount, isColumnHidden } = props;

  return (
    <TableHead>
      <TableRow>
        {props.selectable ? <TableCell padding="checkbox" style={{width: '3em'}}>
          <Checkbox indeterminate={numSelected > 0 && numSelected < rowCount} checked={numSelected === rowCount && rowCount > 0} onChange={onSelectAllClick}/>
        </TableCell> : <TableCell style={{width: '2em', padding: 0}}/>}
        {props.columns && props.columns.map((row: any) => {
            if(isColumnHidden(row)){
                return null;
            }
          return (
            <TableCell key={row.id || row.command} padding='none' sortDirection={orderBy === row.id ? order : false} style={{width: row.width, minWidth: row.width, textAlign: 'center'}}>
            { (row.sortable &&
                <Tooltip title="Sort" placement='bottom-start' enterDelay={300}>
                  <TableSortLabel active={orderBy === row.id} direction={order} onClick={createSortHandler(row.id)}>
                    {row.label}
                  </TableSortLabel>
                </Tooltip>)
                || row.label
            }

            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

const StyledToolbar = styled(Toolbar)<any>(({ theme, numSelected }) => ({
  paddingRight: theme.spacing(),
  flex: 1,
  "& .MuiIconButton-root": {
      color: 'white'
  },
  ...(numSelected > 0 && (
    theme.palette.mode === 'light'
      ? {
          color: theme.palette.secondary.main + " !important",
          backgroundColor: lighten(theme.palette.secondary.light, 0.85) + " !important",
        }
      : {
          color: theme.palette.text.primary + " !important",
          backgroundColor: theme.palette.secondary.dark + " !important",
        }
  ))
}));

const ToolbarTitle = styled('div')({
  flex: '0 0 auto',
});

const ToolbarSpacer = styled('div')({
  flex: '1 1 100%',
});

interface EnhancedTableToolbarProps {
    numSelected: number;
    toolbarClassName?: string;
    columns?: any[];
    selectedActions?: React.ReactNode;
    title?: React.ReactNode;
    addFilter?: (filter: any) => void;
    removeFilter?: (filter: any) => void;
    hideFilter?: boolean;
    canCollapse?: boolean;
    isColumnHidden: (column: any) => boolean;
    toggleColumn: (column: any, hidden: boolean) => void;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const [filterOpen, setFilterOpen] = React.useState<boolean>(false);
  const [columnOpen, setColumnOpen] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<any>(undefined);

  const handleClickOpen = (e: React.MouseEvent) => {
      e.stopPropagation();
    setFilterOpen(true);
    setAnchorEl(e.currentTarget);
  }
  const handleColumnClickOpen = (e: React.MouseEvent) => {
      e.stopPropagation();
    setColumnOpen(true);
    setAnchorEl(e.currentTarget);
  }
  const handleClose = () => {
    setFilterOpen(false);
    setColumnOpen(false);
    setAnchorEl(null);
  }

  var { numSelected } = props;
  const toolbarClass = numSelected > 0 ? undefined : (props.toolbarClassName ?? "lbtoolbar");
  return (
      <StyledToolbar className={toolbarClass} numSelected={numSelected} style={{paddingRight: props.canCollapse ? '50px' : '8px'}}>
          <ToolbarTitle>
            {numSelected > 0 ? (
              <Typography color="inherit" variant="subtitle1">
                {numSelected} selected
              </Typography>
            ) : (
              <Typography color="inherit" variant="h6" id="tableTitle">
                {props.title}
              </Typography>
            )}
          </ToolbarTitle>
          <ToolbarSpacer />
          <div>
            {numSelected > 0 ? (
              <div>{props.selectedActions}</div>
          ) : (<div style={{display: 'flex', flexDirection: 'row'}}>
                {props.hideFilter || !props.columns || props.columns.filter(r => r.toggleable).length === 0 ? '' : <div>
                  <Tooltip title="Show/hide columns">
                    <IconButton
                        aria-label="Show/hide columns"
                        onClick={handleColumnClickOpen}
                        size="large">
                      <ToggleColumnIcon/>
                    </IconButton>
                  </Tooltip>
                  <ColumnDialog onClick={(e: any) => e.stopPropagation()} isColumnHidden={props.isColumnHidden} toggleColumn={props.toggleColumn} open={columnOpen} onClose={handleClose} columns={props.columns} anchorEl={anchorEl}/>
              </div>}
                {props.hideFilter || !props.columns || props.columns.filter(r => r.filterable).length === 0 ? '' : <div>
                  <Tooltip title="Filter results">
                    <IconButton aria-label="Filter results" onClick={handleClickOpen} size="large">
                      <FilterListIcon/>
                    </IconButton>
                  </Tooltip>
                  <FilterDialog onClick={(e: any) => e.stopPropagation()} addFilter={props.addFilter} open={filterOpen} onClose={handleClose} columns={props.columns} anchorEl={anchorEl}/>
              </div>}
              </div>
            )}
          </div>
      </StyledToolbar>
  );
}

const StyledAccordion = styled(Accordion)({
  width: '100%',
  overflow: 'hidden'
});

const StyledTable = styled(Table)({
  width: '100%'
});

const TableWrapper = styled('div')({
  overflowX: 'auto',
  width: '100%'
});

const HighlightDiv = styled('div')<any>(({ theme, selected }) => ({
  ...(selected > 0 && (
    theme.palette.mode === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        }
  ))
}));

export interface EnhancedTableConfig {
    url?: string;
    columns?: any[];
    getData?: (...args: any[]) => any;
    term?: string;
    disableTermParam?: boolean;
    filter?: any;
    keyField?: string | ((item: any) => any);
    pageSize?: number;
    pageSizes?: ReadonlyArray<number | {value: number; label: string}>;
    order?: 'asc' | 'desc';
    orderBy?: string;
    selectable?: boolean;
    actions?: React.ReactNode;
    selectedActions?: React.ReactNode;
    title?: React.ReactNode;
    toolbarClassName?: string;
    hideToolbar?: boolean;
    onFormatData?: (values: any[]) => any;
    onGetData?: (builder: any) => void;
    onLoad?: (data: any[]) => void;
    signalr?: any;
    onSignalrUpdate?: (message: any, data: any[], signalrProps?: any) => any;
    signalrProps?: any;
    filterInUrl?: boolean;
    facets?: any;
    savedSearchEnabled?: boolean;
    savedSearchType?: string;
    tableType?: string;
    refresh?: any;
    _skipRefresh?: boolean;
    setSelected?: (selected: any[], selectedModels: any[]) => void;
    emptyContent?: React.ReactNode;
    [key: string]: any;
}

export interface EnhancedTableProps {
    config: EnhancedTableConfig;
    collapsed?: boolean;
    collapsible?: boolean;
    template?: (args: any) => React.ReactNode;
    wrapperStyle?: React.CSSProperties;
    tableStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    forwardedRef?: React.Ref<any>;
    history?: any;
    location?: any;
    match?: any;
    [key: string]: any;
}

interface EnhancedTableState {
    order: 'asc' | 'desc';
    orderBy: string;
    selected: any[];
    data: any[];
    page: number;
    rowsPerPage: number;
    total: number;
    filter?: any;
    keyField: string | ((item: any) => any);
    filtered: boolean;
    loading: boolean;
    loadedOnce: boolean;
    loaded: boolean;
    expanded: boolean;
    columnSettings: Record<string, boolean>;
    tableType: string;
    initialLoadComplete: boolean;
    allData?: any[];
    facets?: any;
}


// EnhancedTable — converted from a class to a function component with hooks
// (LB-470). Behavior-preserving: the single `state` object mirrors the former
// `this.state`, and a synchronous `stateRef` mirror lets post-`await` reads see
// the just-applied values (the class relied on legacy synchronous setState for
// this; hooks + the ref make it explicit and batching-safe). All former
// instance methods become closures that read live values from `stateRef.current`
// / `propsRef.current`, exactly as the class methods read `this.state` /
// `this.props`. Lifecycle methods map to effects. `shouldComponentUpdate`'s deep
// isEqual is preserved via a React.memo comparator. Both public contracts are
// unchanged: the `forwardedRef` interface object and the `template` render-arg.
const EnhancedTableFunction = (props: EnhancedTableProps) => {
  // Live mirror of props so the stable closures below read the latest props,
  // matching how the class methods read `this.props`.
  const propsRef = React.useRef<EnhancedTableProps>(props);
  propsRef.current = props;

  const getTableType = (): string => {
    // Derive table type from URL path or config
    const path = window.location.pathname;

    if (path.includes('testing')) return 'testing';
    if (path.includes('order-management')) return 'order-management';
    if (path.includes('advanced-product-management')) return 'advanced-product-management';
    if (path.includes('partnermanagementcenter')) return 'partner-management-center';
    if (path.includes('inventory')) return 'inventory';
    if (path.includes('catalog-manager')) return 'catalog-manager';
    if (path.includes('message-center')) return 'message-center';
    if (path.includes('shipment')) return 'shipment';
    if (path.includes('invoice')) return 'invoice';
    if (path.includes('partners')) return 'partners';
    if (path.includes('search')) return 'all-documents';

    // Use config.savedSearchType if provided
    if (props.config.savedSearchType) {
      return props.config.savedSearchType;
    }

    // Fallback to title-based type
    if (props.config.title) {
      const titleBased = (props.config.title as string).toLowerCase().replace(/\s+/g, '-');
      return titleBased;
    }

    return 'default';
  };

  // Initial state mirrors the class field initializer + the constructor overrides
  // (expanded when collapsed, tableType from config or derived).
  const [state, setStateFull] = React.useState<EnhancedTableState>(() => ({
    order: props.config.order || 'desc',
    orderBy: props.config.orderBy || 'id',
    selected: [],
    data: [],
    page: 0,
    rowsPerPage: props.config.pageSize || 10,
    total: 0,
    filter: props.config.filter,
    keyField: props.config.keyField || 'id',
    filtered: false,
    loading: false,
    loadedOnce: false,
    loaded: false,
    expanded: props.collapsed ? false : true,
    columnSettings: {},
    tableType: props.config.tableType || getTableType(),
    initialLoadComplete: false,
  }));

  // Synchronous mirror of state, updated ONLY inside setPartial (before the
  // re-render), so any read after an `await setPartial` sees the applied values
  // without a stale closure — this is what removes the old `await this.setState`
  // batching workaround.
  //
  // Do NOT also re-sync `stateRef.current = state` in the render body. It is
  // redundant — setStateFull is only ever called by setPartial, which already
  // syncs the ref — and under React 18 it CLOBBERS imperative updates: a
  // re-render carrying the pre-update `state` (e.g. a parent setState render
  // interleaving between replaceFilters' setPartial and its getData, as Message
  // Center's Hide Complete toggle does) overwrites the freshly-set ref with the
  // stale value and silently drops the just-injected session filter. The class
  // component this was migrated from read `this.state` directly and never hit it.
  const stateRef = React.useRef<EnhancedTableState>(state);

  // Merge-and-set, mirroring `this.setState(partial)`. Always produces a new
  // object and re-renders (the class always re-rendered subject to SCU; SCU is
  // reproduced by the memo comparator on the export). Never returns a shared ref
  // for a no-op, and never uses an equality-guarded functional updater, so there
  // is no new-ref no-op infinite-loop hazard. Returns a resolved promise so the
  // original `await this.setState(...)` call sites keep their exact shape.
  const setPartial = (partial: Partial<EnhancedTableState>): Promise<void> => {
    const next = { ...stateRef.current, ...partial } as EnhancedTableState;
    stateRef.current = next;
    setStateFull(next);
    return Promise.resolve();
  };

  const getValidPage = (total: number, rowsPerPage: number, currentPage: number): number => {
      const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
      return currentPage > maxPage ? maxPage : currentPage;
  };

  const getKey = (item: any) => {
      let key = item.id;
      if(propsRef.current.config.keyField){
          if(typeof propsRef.current.config.keyField === 'function'){
              key = propsRef.current.config.keyField(item);
          }else{
              key = item[propsRef.current.config.keyField];
          }
      }
      return key;
  };

  const isColumnHidden = (column: any): boolean => {
      let key = column.id || column.command;
      if(!key){
          return column.hidden;
      }
      let setting = stateRef.current.columnSettings[key];
      if(setting === false || setting === true){
          return setting;
      }
      return column.hidden;
  };

  const isSelected = (id: any) => stateRef.current.selected.indexOf(id) !== -1;

  const sortData = (data: any[]): any[] => {
      if(stateRef.current.orderBy){
          data.sort((a, b) => {
              var fieldA = a[stateRef.current.orderBy];
              var fieldB = b[stateRef.current.orderBy];
              if(stateRef.current.order === "desc"){
                  var fa = fieldA;
                  fieldA = fieldB;
                  fieldB = fa;
              }
              if(fieldA < fieldB || fieldA === null || fieldA === undefined){
                  return -1;
              }
              if(fieldA > fieldB){
                  return 1;
              }
              return 0;
          });
      }
      return data;
  };

  const pageData = (data: any[]): any[] => {
      var start = stateRef.current.rowsPerPage * stateRef.current.page;
      return data.slice(start, start + stateRef.current.rowsPerPage);
  };

  const getFilterFunction = (filter: any): (value: any) => boolean => {
      if(filter.filters){
          var functions = filter.filters.map((f: any) => {
              return getFilterFunction(f);
          });
          if(filter.logic === 'or'){
              return (value: any) => !(!(functions.find((f: any) => f(value))));
          }else{
              return (value: any) => functions.every((f: any) => f(value));
          }
      }
      if(Array.isArray(filter.value) && filter.operator !== 'anyeq'){
        if(filter.operator === 'ne'){
          return (value: any) => filter.value.map((r: any) => getFilterFunction({...filter, value: r})).every((r: any) => r(value));
        } else {
          return (value: any) => filter.value.map((r: any) => getFilterFunction({...filter, value: r})).filter((r: any) => r(value)).length > 0;
        }
      }
      if(filter.operator === 'eq'){
          if (filter.value === null) {
              return (value: any) => value[filter.field] === null || value[filter.field] === undefined;
          }
          return (value: any) => value[filter.field] === filter.value;
      }
      else if(filter.operator === 'ne'){
          if (filter.value === null) {
              return (value: any) => value[filter.field] !== null && value[filter.field] !== undefined;
          }
          return (value: any) => value[filter.field] !== filter.value;
      }
      else if(filter.operator === 'gt'){
          return (value: any) => value[filter.field] > filter.value;
      }
      else if(filter.operator === 'lt'){
          return (value: any) => value[filter.field] < filter.value;
      }
      else if(filter.operator === 'le'){
          return (value: any) => value[filter.field] <= filter.value;
      }
      else if(filter.operator === 'ge'){
          return (value: any) => value[filter.field] >= filter.value;
      }
      else if(filter.operator === 'contains'){
          return (value: any) => value[filter.field] && value[filter.field].toLowerCase().indexOf((filter.value || "").toLowerCase()) >= 0;
      }
      else if (filter.operator === 'notcontains'){
          return (value: any) => value[filter.field] && value[filter.field].toLowerCase().indexOf((filter.value || "").toLowerCase()) < 0;
      }
      else if (filter.operator === 'startswith'){
          return (value: any) => value[filter.field] && value[filter.field].toLowerCase().startsWith((filter.value || "").toLowerCase());
      }
      else if (filter.operator === 'endswith'){
            return (value: any) => value[filter.field] && value[filter.field].toLowerCase().endsWith((filter.value || "").toLowerCase());
      }
      else if (filter.operator === 'dateeq') {
          return (value: any) => moment(value[filter.field]).isSame(moment(filter.value), 'day');
      }
      else if(filter.operator === 'datebt'){
          return (value: any) => filter.value && moment(value[filter.field]) >= moment(filter.value[0]) && moment(value[filter.field]) <= moment(filter.value[1]);
      }
      else if (filter.operator === 'anyeq') {
          return (value: any) => {
              // Handle array filter values from dropdowns with getOptions
              const searchValue = Array.isArray(filter.value) && filter.value.length > 0
                  ? filter.value[0]
                  : filter.value;

              if(!Array.isArray(value[filter.field])){
                  return value[filter.field] === searchValue;
              }
              return value[filter.field] && value[filter.field].find((r: any) => r === searchValue)
          };
      }
      else if (filter.operator === 'ingroup') {
          // Handle filtering by group membership
          const groupName = Array.isArray(filter.value) && filter.value.length > 0
              ? filter.value[0]
              : filter.value;

          return (value: any) => {
              // Check if the row's Groups array contains the selected group
              const groups = value.Groups || [];
              return groups.includes(groupName);
          };
      }
      throw new Error(`Operator ${filter.operator} not supported.`);
  };

  const filterData = (data: any[]): any[] => {
      if(stateRef.current.filter){
          var filterFunc = getFilterFunction(stateRef.current.filter);
          data = data.filter(filterFunc);
      }
      return data;
  };

  const pageAndSort = (data: any[]): any[] => {
      sortData(data);
      data = pageData(data);
      return data;
  };

  const getSearch = (): string => {
      let hasFilters = stateRef.current.filter?.filters?.length > 0;
      let currentFilter = hasFilters
          ? stateRef.current.filter.filters.filter((r: any) => r.field && !isSystemFilter(r))
          : undefined;
      let queryParams: any = new Helpers().queryString(window.location.search);
      let query = "";
      let prefix = "q";
      let keys = Object.keys(queryParams).filter(r => r && (r.indexOf(prefix + ".") === 0 || r.indexOf(prefix + "_") === 0));
      Object.keys(queryParams).forEach(key => {
          if(keys.indexOf(key) === -1){
              query += `&${key}=${queryParams[key]}`;
          }
      });
      if(currentFilter){
          for(var i = 0; i < currentFilter.length; i++){
              let value = currentFilter[i].value;
              if(Array.isArray(value)){
                if(value.length === 1){
                  value = value[0];
                } else {
                  value = JSON.stringify(value);
                }
              }
              query += `&${prefix}.${i}=${currentFilter[i].field} ${currentFilter[i].operator} ${window.encodeURIComponent(value)}`;
          }
      }
      if(stateRef.current.order !== propsRef.current.config.order){
           query += `&${prefix}_order=${stateRef.current.order}`;
      }
      if(stateRef.current.orderBy !== propsRef.current.config.orderBy){
           query += `&${prefix}_orderby=${stateRef.current.orderBy}`;
      }
      if(stateRef.current.page !== 0){
           query += `&${prefix}_page=${stateRef.current.page}`;
      }
      if(stateRef.current.rowsPerPage !== propsRef.current.config.pageSize){
           query += `&${prefix}_pagesize=${stateRef.current.rowsPerPage}`;
      }
      let colStates = Object.keys(stateRef.current.columnSettings || {}).map(r => ({id: r, hidden: stateRef.current.columnSettings[r]}));
      colStates = colStates.filter(r => propsRef.current.config.columns.filter((x: any) => x.id && x.id === r.id && x.toggleable && r.hidden !== (x.hidden || false)).length > 0);
      let hidden = colStates.filter(r => r.hidden).map(r => r.id);
      let shown = colStates.filter(r => !r.hidden).map(r => r.id);
      if(hidden.length > 0){
          query += `&${prefix}_hide=${hidden.join(',')}`;
      }
      if(shown.length > 0){
          query += `&${prefix}_show=${shown.join(',')}`;
      }
      if(query.length > 1){
          query = "?" + query.slice(1);
      }
      return query;
  };

  const updateUrlFromFilter = async () => {
      if(propsRef.current.config.filterInUrl !== true){
          return;
      }

      // Don't update URL during initial load
      if (!stateRef.current.initialLoadComplete) {
          return;
      }

      let query = getSearch();

      // Use state to preserve scroll position during URL updates
      propsRef.current.history.replace({
          pathname: propsRef.current.location.pathname,
          search: query,
          state: { preserveScroll: true }
      });
  };

  const getData = async (propsArg?: EnhancedTableProps | null, forceRefresh?: boolean) => {
      var config = (propsArg || propsRef.current).config;
      if(config.url){
          var url = config.url;
          url += url.indexOf("?") === -1 ? "?" : "&";
          let builder: Record<string, string> = {};
          builder.select = "$select=" + config.columns.filter((a: any) => a.id && !a.customData).map((a: any) => a.id).join(',');
          // Consumers that translate the search bar to a synthetic `$filter`
          // (e.g. attachments_v2 for the OData /Attachments endpoint that
          // doesn't honor `term`) can suppress the `&term=` param by setting
          // `config.disableTermParam: true`. The term value still flows
          // through React state so the search input stays controlled, but
          // it's never transmitted to OData.
          if(config.term && !config.disableTermParam){
              builder.term = "&term=" + config.term;
          }
          builder.top = "&$top=" + stateRef.current.rowsPerPage;
          builder.skip = "&$skip=" + (stateRef.current.rowsPerPage * stateRef.current.page);
          if(stateRef.current.filter){
              let context: any = {};
              if(config.columns.filter((r: any) => r.getOptions === getCompanyNames).length > 0){
                  context.partnerGroups = await getPartnerGroups();
              }
              var odataFilter = new FilterOdata(context).ToOData(stateRef.current.filter);
              if(odataFilter){
                  builder.filter = "&$filter=" + odataFilter;
              }
          }
          if(stateRef.current.orderBy){
              builder.orderBy = "&$orderby=" + stateRef.current.orderBy + " " + stateRef.current.order;
          }
          if(config.facets){
              builder.facets = "&facets=" + JSON.stringify(config.facets);
          }
          url += (builder.select || "") + (builder.term || "") + (builder.top || "") + "&$count=true" + (builder.skip || "") + (builder.filter || "") + (builder.orderBy || "") + (builder.facets || "");
          await setPartial({loading: true});
          var res = await axios.get(url);
          setPartial({loading: false, loadedOnce: true});
          var values = res.data.value;
          var facets = res.data.facets || [];

          if(config.onFormatData) {
              const formattedData = await config.onFormatData(values);
              const newTotal = res.data["@odata.count"];
              const validPage = getValidPage(newTotal, stateRef.current.rowsPerPage, stateRef.current.page);
              const pageChanged = validPage !== stateRef.current.page;
              await setPartial({
                  facets: facets,
                  data: formattedData,
                  total: newTotal,
                  page: validPage
              });
              // If page was adjusted, update URL to reflect the change
              // But only if initial load is complete to avoid clearing filters during mount
              if (pageChanged && stateRef.current.initialLoadComplete) {
                  updateUrlFromFilter();
              }
          }
          else {
              const newTotal = res.data["@odata.count"];
              const validPage = getValidPage(newTotal, stateRef.current.rowsPerPage, stateRef.current.page);
              const pageChanged = validPage !== stateRef.current.page;
              await setPartial({
                  facets: facets,
                  data: values,
                  total: newTotal,
                  page: validPage
              });
              if(config.onGetData){
                  config.onGetData(builder)
              }
              // If page was adjusted, update URL to reflect the change
              // But only if initial load is complete to avoid clearing filters during mount
              if (pageChanged && stateRef.current.initialLoadComplete) {
                  updateUrlFromFilter();
              }
          }
      } else if (config.getData){
          if(stateRef.current.allData && !forceRefresh){
              let filtered = filterData(stateRef.current.allData);
              const newTotal = filtered.length;
              const validPage = getValidPage(newTotal, stateRef.current.rowsPerPage, stateRef.current.page);
              await setPartial({
                  data: pageAndSort(filtered),
                  total: newTotal,
                  page: validPage
              });
          }
          else{
              await setPartial({loading: true});
              let data = await config.getData();
              let filtered = filterData(data);
              setPartial({loading: false, loadedOnce: true});
              const newTotal = filtered.length;
              const validPage = getValidPage(newTotal, stateRef.current.rowsPerPage, stateRef.current.page);
              await setPartial({
                  allData: data,
                  data: pageAndSort(filtered),
                  total: newTotal,
                  page: validPage
              });
          }
      }
  };

  const mergeSystemFilters = (newConfigFilter: any) => {
      const currentFilters = stateRef.current.filter?.filters || [];

      // If no config filter provided, preserve all current filters
      if (!newConfigFilter || !newConfigFilter.filters) {
          return stateRef.current.filter || { logic: 'and', filters: currentFilters };
      }

      const newSystemFilters = newConfigFilter.filters.filter((f: any) => isSystemFilter(f)) || [];

      // Separate current filters by type
      const existingUserFilters = currentFilters.filter((f: any) => !isSystemFilter(f) && !isCustomFilter(f));
      const existingCustomFilters = currentFilters.filter((f: any) => isCustomFilter(f));

      // Build merged filter set: new system + existing custom + existing user
      const mergedFilters = [
          ...newSystemFilters,
          ...existingCustomFilters,
          ...existingUserFilters
      ];

      return {
          logic: newConfigFilter.logic || stateRef.current.filter?.logic || 'and',
          filters: mergedFilters
      };
  };

  const onAddFilter = async (filter: any) => {
      let currentFilter: any = {...stateRef.current.filter};
      if(!stateRef.current.filtered){
          let arr = currentFilter && (Object.keys(currentFilter).length > 0 || currentFilter.length) ? [currentFilter] : [];
          currentFilter = {filters: arr, logic: 'and'}
      }
      if(!currentFilter.filters){
          currentFilter.filters = [];
      }
      currentFilter.filters = [...currentFilter.filters];
      if(filter && (Object.keys(filter).length > 0 || filter.length)){
          currentFilter.filters.push(filter);
      }
      await setPartial({filter: currentFilter, filtered: true, page: 0});
      updateUrlFromFilter();
      await getData();
  };

  const onRemoveFilter = async (filter: any) => {
      let currentFilter: any = {...stateRef.current.filter};
      currentFilter.filters = currentFilter.filters.filter((r: any) => r.operator !== filter.operator || r.value !== filter.value || r.field !== filter.field);
      await setPartial({filter: currentFilter, page: 0});
      updateUrlFromFilter();
      await getData();
  };

  /* Bulk removal of filters (removeFilters, addFilters), only needed for use in child/template components.
   * Calling onAddFilter in a loop from a child results in state mismatch. */
  const addFilters = async (filters: any[]) => {
        let currentFilter: any = {...stateRef.current.filter};
        if(!stateRef.current.filtered){
            let arr = currentFilter && (Object.keys(currentFilter).length > 0 || currentFilter.length) ? [currentFilter] : [];
            currentFilter = {filters: arr, logic: 'and'}
        }
        if(!currentFilter.filters){
            currentFilter.filters = [];
        }
        currentFilter.filters = [...currentFilter.filters];
        if(filters && filters.length > 0){
            currentFilter.filters = currentFilter.filters.concat(filters);
        }
        await setPartial({filter: currentFilter, filtered: true, page: 0});
        updateUrlFromFilter();
        await getData();
    };
  const removeFilters = async (filters: any[]) => {
        let currentFilter: any = {...stateRef.current.filter};
        currentFilter.filters = currentFilter.filters.filter((filter: any) => !filters.includes(filter));
        await setPartial({filter: currentFilter, page: 0});
        updateUrlFromFilter();
        await getData();
  };
  const updateFilter = async (filter: any) => {
        let currentFilter: any = {...stateRef.current.filter};
        currentFilter = removeFiltersByField(currentFilter, filter.field);
        // Add the new filter
        currentFilter.filters.push(filter);
        await setPartial({filter: currentFilter, page: 0});
        updateUrlFromFilter();
        await getData();
  };
  const replaceFilters = async (filters: any[]) => {
        let currentFilter: any = {...stateRef.current.filter};
        currentFilter.filters = filters;
        await setPartial({filter: currentFilter, filtered: true, page: 0});
        updateUrlFromFilter();
        await getData();
  };

  const applyBatchedState = async (batchedChanges: any) => {
    const newState: any = {};

    if (batchedChanges.filters) {
      let currentFilter: any = {...stateRef.current.filter};
      currentFilter.filters = batchedChanges.filters;
      newState.filter = currentFilter;
      newState.filtered = true;
      newState.page = 0;
    }

    if (batchedChanges.sorting) {
      if (batchedChanges.sorting.orderBy !== undefined) {
        newState.orderBy = batchedChanges.sorting.orderBy;
      }
      if (batchedChanges.sorting.order !== undefined) {
        newState.order = batchedChanges.sorting.order;
      }
    }

    if (batchedChanges.pagination) {
      if (batchedChanges.pagination.pageSize !== undefined) {
        newState.rowsPerPage = batchedChanges.pagination.pageSize;
        // Reset to first page when page size changes
        if (!batchedChanges.filters) {
          newState.page = 0;
        }
      }
    }

    if (batchedChanges.columnSettings) {
      newState.columnSettings = batchedChanges.columnSettings;
    }

    if (Object.keys(newState).length > 0) {
      await setPartial(newState);
      updateUrlFromFilter();
      await getData();
    }
  };

  const handleRequestSort = async (event: any, property: string) => {

    const orderBy = property;
    let order: 'asc' | 'desc' = 'desc';

    if (stateRef.current.orderBy === property && stateRef.current.order === 'desc') {
      order = 'asc';
    }

    await setPartial({ order, orderBy });
    await getData();
    updateUrlFromFilter();
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setPartial({ selected: stateRef.current.data.map(n => {
          var key = n.id;
          if(propsRef.current.config.keyField){
              if(typeof propsRef.current.config.keyField === 'function'){
                  key = propsRef.current.config.keyField(n);
              }else{
                  key = n[propsRef.current.config.keyField as string];
              }
          }
          return key;
      })});
      return;
    }
    setPartial({ selected: [] });
  };

  const handleClick = (event: any, id: any) => {
    const { selected } = stateRef.current;
    const selectedIndex = selected.indexOf(id);
    let newSelected: any[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setPartial({ selected: newSelected });
  };

  const handleChangePage = async (evt: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
      if(evt){ // if null then this was not triggered from user action
          await setPartial({ page, selected: [] });
          await getData();
          updateUrlFromFilter();
      }
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    // Preserve pre-migration behavior: the raw <select> string value is stored in
    // state as-is (no parseInt coercion). Typed `any` to keep the original
    // untyped-JS runtime behavior without adding a coercion to back the `number`
    // state type.
    const newRowsPerPage: any = event.target.value;
    const newPage = getValidPage(stateRef.current.total, newRowsPerPage, stateRef.current.page);

    await setPartial({
      rowsPerPage: newRowsPerPage,
      page: newPage
    });
    await getData();
    updateUrlFromFilter();
  };

  const toggleColumn = async (column: any, columnState: boolean) => {
      let key = column.id || column.command;
      let settings = {...stateRef.current.columnSettings};
      settings[key] = columnState;
      await setPartial({columnSettings: settings});
      updateUrlFromFilter();
  };

  const getCurrentTableConfig = () => {
    // Get current filters and exclude system filters
    const currentFilters = stateRef.current.filter?.filters || [];

    // For saved searches, we want to exclude system filters but keep custom and user filters
    const nonSystemFilters = extractNonSystemFilters(currentFilters);

    // Get column visibility
    const visibleColumns: string[] = [];
    const hiddenColumns: string[] = [];
    if (propsRef.current.config.columns) {
      propsRef.current.config.columns.forEach((col: any) => {
        const key = col.id || col.command;
        // Only save toggleable columns
        if (key && col.toggleable) {
          const isHidden = isColumnHidden(col);
          if (isHidden) {
            hiddenColumns.push(key);
          } else {
            visibleColumns.push(key);
          }
        }
      });
    }

    return {
      filters: {
        logic: stateRef.current.filter?.logic || 'and',
        filters: nonSystemFilters
      },
      columns: {
        visible: visibleColumns,
        hidden: hiddenColumns
      },
      sorting: {
        orderBy: stateRef.current.orderBy,
        order: stateRef.current.order
      },
      pagination: {
        pageSize: stateRef.current.rowsPerPage
      },
      searchTerm: propsRef.current.config.term || ''
    };
  };

  const handleSavedSearchApplied = (savedConfig: any, isInitialLoad: boolean = false) => {
    // Only clear existing URL filters if this is NOT an initial load
    // During initial load, we want to preserve any filters that were in the URL
    if (propsRef.current.config.filterInUrl && !isInitialLoad) {
      const params = new URLSearchParams(window.location.search);
      let index = 0;
      while (params.has(`q.${index}`)) {
        params.delete(`q.${index}`);
        index++;
      }
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }

    // Apply config using shared utility. `self` reconstructs the interface the
    // class exposed as `this` for applySavedSearchConfig (applyBatchedState /
    // setState / getData / replaceFilters / state).
    const self = {
      replaceFilters,
      addFilters,
      removeFilters,
      updateFilter,
      onAddFilter,
      onRemoveFilter,
      applyBatchedState,
      setState: (newState: any) => setPartial(newState),
      getData,
      state: stateRef.current
    };
    applySavedSearchConfig(savedConfig, {
      self: self,
      columns: propsRef.current.config.columns
    });
  };

  const validateFiltersForCompany = async (filters: any[], queryParams: any) => {
      const validatedFilters = [];
      let shouldClearSavedSearch = false;

      for (const filter of filters) {
          const col = propsRef.current.config.columns.find((c: any) => c.id === filter.field);

          // Skip validation for ingroup filters - their values are group names,
          // not individual company names from getOptions
          if (filter.operator === 'ingroup') {
              validatedFilters.push(filter);
              continue;
          }

          if (col && col.getOptions) {
              try {
                  const options = await col.getOptions(stateRef.current.data);
                  const validValues = options.map((o: any) => o.value);

                  if (Array.isArray(filter.value)) {
                      const validFilterValues = filter.value.filter((v: any) => validValues.includes(v));
                      if (validFilterValues.length > 0) {
                          filter.value = validFilterValues;
                          validatedFilters.push(filter);
                      } else {
                          console.warn(`Filter for ${filter.field} with values ${filter.value} is no longer valid, removing`);
                      }
                  } else if (validValues.includes(filter.value)) {
                      validatedFilters.push(filter);
                  } else {
                      console.warn(`Filter for ${filter.field} with value ${filter.value} is no longer valid, removing`);
                  }
              } catch (error) {
                  console.error(`Error validating filter for ${filter.field}:`, error);
              }
          } else {
              validatedFilters.push(filter);
          }
      }

      if (shouldClearSavedSearch) {
          const params = new URLSearchParams(window.location.search);
          params.delete('savedSearch');
          const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
          window.history.replaceState({}, '', newUrl);
      }

      return validatedFilters;
  };

  const updateFilterFromUrl = async (): Promise<boolean> => {
      let queryParams: any = new Helpers().queryString(propsRef.current.location.search);
      let prefix = "q";
      let keys = Object.keys(queryParams).filter(r => r && r.indexOf(prefix + ".") === 0);
      let filter: any[] = [];
      let orderBy = stateRef.current.orderBy;
      let order = stateRef.current.order;
      let page = stateRef.current.page;
      let pageSize = stateRef.current.rowsPerPage;
      let columnSettings: Record<string, boolean> = {...stateRef.current.columnSettings};
      let changed = false;
      let orderByUrl = queryParams[prefix + "_orderby"];
      let orderUrl = queryParams[prefix + "_order"];
      let pageUrl: any = queryParams[prefix + "_page"];
      let pageSizeUrl: any = queryParams[prefix + "_pagesize"];
      let shownCols = queryParams[prefix + "_show"];
      let hiddenCols = queryParams[prefix + "_hide"];
      pageUrl = isNaN(pageUrl) ? undefined : parseInt(pageUrl);
      pageSizeUrl = isNaN(pageSizeUrl) ? undefined : parseInt(pageSizeUrl);
      shownCols = shownCols ? shownCols.split(",") : [];
      hiddenCols = hiddenCols ? hiddenCols.split(",") : [];
      if(orderByUrl !== orderBy || orderUrl !== order || pageUrl !== page || pageSizeUrl !== pageSize || shownCols.length > 0 || hiddenCols.length > 0){
          changed = true;
          order = orderUrl ? orderUrl : propsRef.current.config.order;
          orderBy = orderByUrl ? orderByUrl : propsRef.current.config.orderBy;
          page = pageUrl ? pageUrl : 0;
          pageSize = pageSizeUrl ? pageSizeUrl : (propsRef.current.config.pageSize || 10);
          shownCols.forEach((id: string) => {
             let matchingCol = propsRef.current.config.columns.find((r: any) => r.id && r.id === id && r.toggleable);
             if(matchingCol){
                 columnSettings[matchingCol.id] = false;
             }
          });
          hiddenCols.forEach((id: string) => {
             let matchingCol = propsRef.current.config.columns.find((r: any) => r.id &&  r.id === id && r.toggleable);
             if(matchingCol){
                 columnSettings[matchingCol.id] = true;
             }
          });
      }
      keys.forEach(key => {
          let split = queryParams[key].split(" ");
          let field = split[0];
          let operator = split[1];
          let value: any = split.slice(2).join(" ");
          let col = propsRef.current.config.columns.find((r: any) => r.id === field);
          // Skip if column not found
          if (!col) {
              return;
          }
          let fieldType = getFieldType(col.type);
          if(value && value.startsWith("[") && value.endsWith("]")){
            try{
              value = JSON.parse(value);
            }catch{}
          }
          if(fieldType === "number" && !isNaN(value)){
              value = parseFloat(value);
          }
          if(fieldType === "date" || fieldType === "localdate"){
            if(Array.isArray(value)){
              value = value.map((r: any) => moment(r).toDate());
            } else {
              value = moment(value).toDate();
            }
          }
          if(fieldType === "bool"){
              value = (value || "").toLowerCase().trim() === "true";
          }
          if(value === "null"){
              value = null;
          }
          const filterObj = createFilter(field, operator, value);
          filter.push(filterObj);
      });

      const originalFilterCount = filter.length;
      filter = await validateFiltersForCompany(filter, queryParams);
      const filtersWereRemoved = filter.length < originalFilterCount;

      if (filtersWereRemoved) {
          const params = new URLSearchParams(window.location.search);
          let index = 0;
          while (params.has(`${prefix}.${index}`)) {
              params.delete(`${prefix}.${index}`);
              index++;
          }
          filter.forEach((f, i) => {
              let value = f.value;
              if (Array.isArray(value)) {
                  if (value.length === 1) {
                      value = value[0];
                  } else {
                      value = JSON.stringify(value);
                  }
              }
              params.set(`${prefix}.${i}`, `${f.field} ${f.operator} ${window.encodeURIComponent(value)}`);
          });
          const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
          window.history.replaceState({}, '', newUrl);
      }

      let currentFilter: any = stateRef.current.filter;

      if(!stateRef.current.filtered && filter.length > 0){
          let arr = currentFilter?.filters || [];
          currentFilter = {filters: arr, logic: currentFilter?.logic || 'and'};
          currentFilter.filters = currentFilter.filters.concat(filter);
          await setPartial({filtered: true, filter: currentFilter, order: order, orderBy: orderBy, page: page, rowsPerPage: pageSize, columnSettings});
          await getData();
          return true;
      } else if(stateRef.current.filtered && filter.length === 0 && currentFilter.filters && currentFilter.filters.filter((r: any) => r.field).length > 0){
          await setPartial({filtered: false, filter: propsRef.current.config.filter, order: order, orderBy: orderBy, page: page, rowsPerPage: pageSize, columnSettings});
          await getData();
          return true;
      } else if(changed){
          await setPartial({order: order, orderBy: orderBy, page: page, rowsPerPage: pageSize, columnSettings});
          await getData();
          return true;
      }
      return false;
  };

  const updateSignalr = async (message: any) => {
      if(propsRef.current.config.onSignalrUpdate){
          var updates = await propsRef.current.config.onSignalrUpdate(message, stateRef.current.data, propsRef.current.config.signalrProps);
          if(updates === 'refresh'){
              let i = 0;
              while(stateRef.current.loading && i < 10){
                  i++;
                  await new Promise(r => setTimeout(r, 1000));
              }
              if(!stateRef.current.loading){
                  getData(propsRef.current, true);
              }
              return;
          }
          if(!(updates instanceof Array)){
              updates = [updates];
          }
          var findItem = (key: any) => (r: any) => r[stateRef.current.keyField as string] === key;
          let updated = false;
          let data = [...stateRef.current.data];
          for(var i = 0; i < updates.length; i++){
              let newData = updates[i];
              if(!newData){
                  continue;
              }
              let existingIndex = data.findIndex(findItem(newData[stateRef.current.keyField as string]));
              if(existingIndex < 0){
                  continue;
              }
              data[existingIndex] = {...data[existingIndex], ...newData};
              updated = true;
          }
          if(updated){
              setPartial({data: data});
          }
      }
  };
  // Keep a stable wrapper reading the latest updateSignalr closure so the
  // signalr subscription can be registered/unregistered by reference (mirrors
  // the stable instance method the class registered).
  const updateSignalrRef = React.useRef(updateSignalr);
  updateSignalrRef.current = updateSignalr;

  // Build the imperative interface exposed via forwardedRef. Rebuilt on every
  // commit — the class set this in both componentDidMount and componentDidUpdate.
  const setForwardedRef = () => {
      if (propsRef.current.forwardedRef) {
          (propsRef.current.forwardedRef as any).current = {
              state: stateRef.current,
              extensions: {
                  replaceFilters: replaceFilters,
                  addFilters: addFilters,
                  removeFilters: removeFilters,
                  updateFilter: updateFilter,
                  onAddFilter: onAddFilter,
                  onRemoveFilter: onRemoveFilter,
                  applyBatchedState: applyBatchedState,
                  setState: (newState: any) => setPartial(newState)
              },
              getData: getData,
              refresh: () => getData(null, true)
          };
      }
  };

  // componentDidMount + componentDidUpdate both refreshed the forwardedRef
  // interface; a no-deps effect runs after every commit to match that.
  React.useEffect(() => {
      setForwardedRef();
  });

  // componentDidMount: initial data load + URL-filter hydration, then mark the
  // initial load complete and fire onLoad.
  React.useEffect(() => {
      (async () => {
          var loaded = false;
          if(propsRef.current.config.filterInUrl){
              loaded = await updateFilterFromUrl();
          }
          if(!loaded){
              await getData();
          }
          // Set initialLoadComplete to true
          setPartial({loaded: true, initialLoadComplete: true});
          if(propsRef.current.config && propsRef.current.config.onLoad){
              propsRef.current.config.onLoad(stateRef.current.data);
          }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidMount + componentWillUnmount + the "signalr appeared" branch of
  // componentDidUpdate: subscribe to the signalr `notify` event whenever a
  // connection is present, and unsubscribe on unmount / connection change.
  const signalrConn = propsRef.current.config ? propsRef.current.config.signalr : undefined;
  React.useEffect(() => {
      if(!signalrConn){
          return;
      }
      const listener = (message: any) => updateSignalrRef.current(message);
      signalrConn.on('notify', listener);
      return () => {
          signalrConn.off('notify', listener);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signalrConn]);

  // componentDidUpdate: runs after every commit except the first. Compares the
  // current props against the previous render's props (prevPropsRef), mirroring
  // the class's `componentDidUpdate(prevProps)` exactly. Every branch is guarded
  // by a prevProps comparison so the effect settles (no infinite loop).
  const didMountRef = React.useRef(false);
  const prevPropsRef = React.useRef(props);
  React.useEffect(() => {
      if (!didMountRef.current) {
          didMountRef.current = true;
          prevPropsRef.current = props;
          return;
      }
      const prevProps = prevPropsRef.current;

      // Update tableType if it changes in config
      if (propsRef.current.config.tableType && propsRef.current.config.tableType !== prevProps.config.tableType) {
          setPartial({ tableType: propsRef.current.config.tableType });
      }

      (async () => {
          var refresh = false;
          var forceRefresh = false;
          var refreshed = false;
          // NOTE: the class's "signalr appeared" re-subscribe branch is handled
          // by the [signalrConn] effect above, so it is intentionally omitted here
          // to avoid a double subscription.
          if(!isEqual(propsRef.current.config.filter, prevProps.config.filter)){
              // Skip update if both are null/undefined to avoid clearing URL filters
              if (!propsRef.current.config.filter && !prevProps.config.filter) {
                  // Don't update filters when config filter remains null
              } else {
                  // For non-system filters (like our shipment options filter),
                  // just use the config filter directly without merging
                  const hasSystemFilters = propsRef.current.config.filter?.filters?.some((f: any) => f.system === true);

                  if (hasSystemFilters && (stateRef.current.filtered || (stateRef.current.filter && stateRef.current.filter.filters && stateRef.current.filter.filters.length > 0))) {
                      // Only merge if we have system filters and existing filters
                      const mergedFilter = mergeSystemFilters(propsRef.current.config.filter);
                      await setPartial({filter: mergedFilter, filtered: mergedFilter.filters.length > 0});
                  } else {
                      // For regular filters or when clearing, just use the config filter directly
                      await setPartial({filter: propsRef.current.config.filter, filtered: !!(propsRef.current.config.filter && propsRef.current.config.filter.filters && propsRef.current.config.filter.filters.length > 0)});
                  }
                  refresh = true;
              }
          }
          if(propsRef.current.config.filterInUrl && stateRef.current.initialLoadComplete &&
             propsRef.current.location.search !== prevProps.location.search &&
             propsRef.current.location.search !== getSearch()){
              refreshed = await updateFilterFromUrl();
          }
          if ((prevProps.config.term !== propsRef.current.config.term && !propsRef.current.config._skipRefresh) || prevProps.config.refresh !== propsRef.current.config.refresh) {
              refresh = true;
              forceRefresh = prevProps.config.refresh !== propsRef.current.config.refresh;
          }
          // Clear the skip refresh flag after checking
          if (propsRef.current.config._skipRefresh) {
              delete propsRef.current.config._skipRefresh;
          }
          if(prevProps.config.term !== propsRef.current.config.term && stateRef.current.selected && stateRef.current.selected.length > 0){
              setPartial({ selected: [] });
          }
          if(propsRef.current.config.setSelected){
              let selectedModels = stateRef.current.data.filter(n => {
                  let key = getKey(n);
                  return stateRef.current.selected.indexOf(key) >= 0;
              });
              propsRef.current.config.setSelected(stateRef.current.selected, selectedModels);
          }
          if(refresh && !refreshed){
              await getData(null, forceRefresh);
              if(stateRef.current.selected && stateRef.current.selected.length > 0){
                  let stillExist = stateRef.current.data.filter(r => stateRef.current.selected.indexOf(getKey(r)) !== -1);
                  if(stillExist.length !== stateRef.current.selected.length){
                      setPartial({selected: stillExist});
                  }
              }
              if(propsRef.current.config && propsRef.current.config.onLoad && stateRef.current.page === 0){
                  propsRef.current.config.onLoad(stateRef.current.data);
              }
          }
      })();

      prevPropsRef.current = props;
  });

  if (props.template) {
      return props.template({
        data: state.data,
        config: props.config,
        extensions: {
            isSelected: isSelected,
            handleClick: handleClick,
            onAddFilter: onAddFilter,
            addFilters: addFilters,
            onRemoveFilter: onRemoveFilter,
            removeFilters: removeFilters,
            updateFilter: updateFilter,
            replaceFilters: replaceFilters,
            applyBatchedState: applyBatchedState,
            isColumnHidden: isColumnHidden,
            toggleColumn: toggleColumn,
            // Pre-existing (confirmed via `git show ee28306a`): `createSortHandler`
            // is only ever defined on the separate EnhancedTableHead component,
            // never on this one, so `this.createSortHandler` always evaluated to
            // `undefined` here. Preserved as `undefined` (no consumer reads it).
            createSortHandler: undefined,
            handleChangePage: handleChangePage,
            handleChangeRowsPerPage: handleChangeRowsPerPage,
            handleSelectAllClick: handleSelectAllClick,
            handleRequestSort: handleRequestSort,
            pageAndSort: pageAndSort,
            getData: getData,
            setState: (newState: any) => setPartial(newState)
        },
        state: state
      })
    }

    const { data, order, orderBy, selected, rowsPerPage, page, loadedOnce } = state;
    // Pre-existing behavior (confirmed via `git show ee28306a`): this reads
    // `state.count`, a key this component never sets (the real running
    // total lives in `state.total`). `state.count` is always
    // `undefined`, so `emptyRows` is always `NaN` and the filler row a few lines
    // down (which would pad a short last page out to a full page's height) never
    // renders. Left reading `count` (cast, since it's not on the state type) to
    // preserve the exact pre-migration behavior — same treatment as the
    // `userFiltered` typo below. Switching to `state.total` would activate an
    // unreviewed app-wide filler-row visual change, out of scope for a pure
    // TS migration.
    const emptyRows = rowsPerPage - Math.min(rowsPerPage, (state as any).count - page * rowsPerPage);
    let canCollapse = props.collapsible && selected.length === 0;
    let lowerUrl = ((props.config || {}).url || "").toLowerCase();
    const hideToolbar = !!props.config.hideToolbar;
    // disable last page button if we're looking at over 100,000 items in Azure Search
    let disableLastPage = state.total > 100000 && lowerUrl.indexOf("/functions.") > 0 && lowerUrl.indexOf("search") > 0;

    return (
        <>
            {/* Add SavedSearchManager if explicitly enabled */}
            {props.config.savedSearchEnabled === true && (
              <SavedSearchManager
                tableType={state.tableType}
                tableState={getCurrentTableConfig()}
                initialFilters={props.config.filter}
                onConfigApplied={handleSavedSearchApplied}
                style={{ width: '100%', marginBottom: '1rem' }}
              />
            )}
            <StyledAccordion expanded={state.expanded} onChange={(e: any, expanded: boolean) => {canCollapse && setPartial({expanded: expanded})}}>
              <AccordionSummary
                  style={hideToolbar ? {display: 'none'} : {display: 'flex', cursor: canCollapse ? undefined : 'auto'}}
                  expandIcon={!canCollapse || hideToolbar ? undefined : <ExpandMore/>}>
                  <EnhancedTableToolbar
                      numSelected={selected.length}
                      columns={props.config.columns}
                      selectedActions={props.config.selectedActions}
                      title={props.config.title}
                      toolbarClassName={props.config.toolbarClassName}
                      addFilter={onAddFilter}
                      removeFilter={onRemoveFilter}
                      hideFilter={!state.expanded}
                      canCollapse={canCollapse}
                      isColumnHidden={isColumnHidden}
                      toggleColumn={toggleColumn}
                      />
                </AccordionSummary>
                <AccordionDetails style={{display: 'block', padding: 0}}>
                <HighlightDiv selected={selected.length}>
                {/* Pre-existing (confirmed via `git show ee28306a`): `state.userFiltered` is
                    read here but this component never sets a `userFiltered` key (only
                    `filtered`), so this has always evaluated to `undefined` and the
                    '0.5em' branch never applied. Unlike the `state.total` typo below,
                    it's not obvious `filtered` is the intended replacement here (it
                    would also fire for config-supplied/system filters, not just
                    user-added ones), so this is left as a documented `any`-cast
                    rather than guessed at. */}
                {props.config.actions ? <HighlightDiv selected={selected.length} style={{display: 'flex', flexDirection: 'row', paddingTop: '1em', marginBottom: (state as any).userFiltered && state.filter.filters ? '0.5em' : '0'}}>
                <div style={{paddingLeft: '1em', paddingRight: '1em', display: 'flex', flexDirection: 'row', visibility: selected.length > 0 ? 'hidden' : undefined, flex: 1}} >
                {props.config.actions}
                </div>
              </HighlightDiv>: ''}
              <div style={{display: 'flex', flexDirection: 'row', paddingLeft: '1em'}} className={classNames({[selected.length > 0 ? 'highlight' : '']: selected.length > 0})}>
              {state.filtered && state.filter.filters ?
                    state.filter.filters.filter((r: any) => r.field && !r.filters && !isSystemFilter(r)).map((item: any, index: number) => {
                        let column = props.config.columns.find((r: any) => r.id === item.field);
                        // Skip filters without column definitions
                        if (!column) {
                          return null;
                        }
                        let opName = getOperators(column).find(r => r.op === item.operator).label;
                        let value = item.value;
                        if(value instanceof Date){
                          value = moment(item.value).format('MM/DD/YYYY');
                        }
                        if(Array.isArray(value)){
                          if(value.every((r: any) => r instanceof Date)){
                            value = value.map((r: any) => moment(r).format('MM/DD/YYYY')).join(" and ");
                          } else {
                            value = value.join(" or ");
                          }
                        }
                        return (<Chip style={{visibility: selected.length > 0 ? 'hidden' : undefined, marginTop: '0.5rem', marginRight: '0.5rem', maxWidth: '30rem', textOverflow: 'ellipsis'}} key={index} onDelete={() => onRemoveFilter(item)} label={column.label + ' ' + opName + ' ' + value} title={column.label + ' ' + opName + ' ' + value}></Chip>);
                    })
                  : ''}
            </div>
                </HighlightDiv>
                <LinearProgress style={{flex:1, height: '0.2em', marginTop: '0.2em', visibility: state.loading ? undefined : 'hidden'}} variant='indeterminate'/>
                <TableWrapper style={props.wrapperStyle}>
                  <StyledTable style={props.tableStyle} aria-labelledby="tableTitle">
                    <EnhancedTableHead
                      numSelected={selected.length}
                      order={order}
                      orderBy={orderBy}
                      onSelectAllClick={handleSelectAllClick}
                      onRequestSort={handleRequestSort}
                      rowCount={data.length}
                      columns={props.config.columns}
                      selectable={props.config.selectable}
                      isColumnHidden={isColumnHidden}
                    />
                    <TableBody  style={props.bodyStyle}>
                      {data.map(n => {
                          var key = n.id;
                          if(props.config.keyField){
                              if(typeof props.config.keyField === 'function'){
                                  key = props.config.keyField(n);
                              }else{
                                  key = n[props.config.keyField as string];
                              }
                          }
                          const isRowSelected = isSelected(key);
                          return (
                            <TableRow
                              hover
                              onClick={event => props.config.selectable && handleClick(event, key)}
                              role="checkbox"
                              aria-checked={isRowSelected}
                              tabIndex={-1}
                              key={key}
                              selected={isRowSelected}
                            >
                            {props.config.selectable ? <TableCell padding="checkbox">
                              <Checkbox checked={isRowSelected} />
                            </TableCell> : <TableCell style={{width: '2em', padding: 0}}/>}
                            {props.config.columns.map((c: any) => {
                                if(isColumnHidden(c)){
                                    return null;
                                }
                                let content = c.template ? c.template(n[c.id], n, onAddFilter) : n[c.id];
                                let style: any = {width: c.width, minWidth: c.width, textAlign: c.align || 'center'};
                                if(!c.overflow){
                                    style.textOverflow = 'ellipsis';
                                    style.overflow = 'hidden';
                                    style.whiteSpace = 'nowrap';
                                    style.maxWidth = 0;
                                }
                                style = {...style, ...c.style};
                                return (
                                    <TableCell component="td" scope="row" padding="none" key={c.id || c.command} style={style}>
                                      {c.stopPropagation ? <div onClick={(e) => {e.stopPropagation()}}>{content}</div> : content}
                                    </TableCell>
                                );
                            })}
                            </TableRow>
                          );
                        })}
                    {data.length === 0 && state.page === 0 && loadedOnce &&
                        <TableRow style={{ height: '5rem' }}>
                          <TableCell colSpan={props.config.columns.length + 2} style={{textAlign: 'center'}}>{props.config.emptyContent ? props.config.emptyContent : 'No results found.'}</TableCell>
                        </TableRow>}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 49 * emptyRows }}>
                          <TableCell colSpan={props.config.columns.length + 2} />
                        </TableRow>
                      )}
                    </TableBody>
                  </StyledTable>
                </TableWrapper>
                <TablePagination
                  component="div"
                  count={state.total}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={props.config.pageSizes}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  // Pre-migration behavior preserved: MUI's TablePagination treats
                  // `slots.actions` as a sub-slot container (not a full actions-
                  // component override), so this render function is ignored at
                  // runtime and the app theme's `ThemeTablePaginationActions`
                  // (registered as the default `ActionsComponent`) renders the pager.
                  // Cast to satisfy the sub-slot type while keeping the original
                  // runtime behavior; switching to `ActionsComponent` would change
                  // behavior (activate the local `disableLastPage` guard), out of
                  // scope for a pure TS migration.
                  slots={{
                      actions: ((subProps: any) => <TablePaginationActions disableLastPage={disableLastPage} {...subProps}/>) as any
                  }}
                />
                </AccordionDetails>
              </StyledAccordion>
        </>
    );
};

// shouldComponentUpdate(nextProps, nextState) returned
//   !isEqual(props, next) || !isEqual(props.config, next.config) || !isEqual(state, next)
// i.e. re-render whenever props OR config OR state differ deeply. State changes
// come from useState and always re-render, so the memo comparator only needs to
// reproduce the props/config half: skip (return true) only when both props and
// config are deeply equal. `forwardedRef` is a stable ref object (same identity
// across renders) so isEqual short-circuits on it without diving into `.current`.
const arePropsEqual = (prevProps: EnhancedTableProps, nextProps: EnhancedTableProps): boolean => {
  return isEqual(prevProps, nextProps) && isEqual(prevProps.config, nextProps.config);
};

const EnhancedTableMemo = React.memo(EnhancedTableFunction, arePropsEqual);

const EnhancedTable = React.forwardRef<any, EnhancedTableProps>((props: EnhancedTableProps, ref) => {
  return <EnhancedTableMemo {...props} forwardedRef={ref} />;
});

// withRouter's TS overload narrows the wrapped component's exposed props to
// (effectively) `Pick<RouteComponentProps, never>`, hiding the actual
// config/template/collapsible/etc. surface — the same issue already
// documented for this component's consumers in standards.tsx and
// attachments_v2.tsx. Casting the *input* to `any` isn't enough to avoid
// that: the resulting export must also be widened back to
// `React.ComponentType<any>` here so the ~90 existing call sites across the
// app (which pass wildly different `config` shapes and were previously
// covered by table.d.ts's `React.ComponentType<any>`) keep compiling without
// each of them adding their own cast. Behavior unchanged.
export default withRouter(EnhancedTable as any) as React.ComponentType<any>;
